import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Menu, Monitor, Moon, Sun } from 'lucide-react'
import { api } from '../lib/api'
import {
  esteSolicitare,
  esteTaskTerminat,
  useCitesteNotificarea,
  useCitesteToateNotificarile,
} from '../lib/notificari'
import { useAuthStore } from '../stores/auth'
import { alegeTema, temaAleasa, type Tema } from '../lib/tema'

export function Header({ onMenuClick }: { onMenuClick?: () => void } = {}) {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const clear = useAuthStore((s) => s.clear)
  // Doar adminii cu dreptul personal (01.11.2026): altfel „Tokenul meu de API" nici nu apare.
  const poateToken = useAuthStore(
    (s) => s.user?.role === 'admin' && s.permissions.includes('tasks.export:token'),
  )
  const [open, setOpen] = useState(false)
  const [tema, setTema] = useState<Tema>(temaAleasa)

  const onLogout = async () => {
    try {
      await api.post('/auth/logout')
    } catch {
      // ignore
    }
    clear()
    toast.success('Te-ai delogat')
    navigate('/login', { replace: true })
  }

  if (!user) return null

  const initials = user.name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()

  /*
    Fara traseu de navigare sub antet (Madalin, 09.10.2026). Era compus din adresa paginii, asa
    ca „Marketing" si „Shopify" duceau la /marketing si /marketing/shopify — pagini care nu
    exista, deci 404. Meniul din stanga arata oricum unde esti.
  */
  return (
    <>
      <header className="flex items-center justify-between bg-[#1a1a1a] px-3 py-2.5 text-white md:px-6">
        <div className="flex min-w-0 items-center gap-2 md:gap-3">
          {onMenuClick && (
            <button
              type="button"
              onClick={onMenuClick}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-white/80 hover:bg-white/10 lg:hidden"
              aria-label="Deschide meniul"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
          {/*
            Logo-ul duce la Dashboard, ca pe orice site (Madalin, 09.10.2026). Fara `title`:
            Safari lasa indiciul agatat in mijlocul paginii dupa navigare.
          */}
          <Link
            to="/dashboard"
            aria-label="Boost4Life — mergi la Dashboard"
            className="-mx-1 flex items-center gap-2 rounded px-1 py-0.5 transition hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/60"
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-b4l-green text-xs font-bold">
              B4L
            </div>
            <span className="text-sm font-medium tracking-wide">Boost4Life</span>
          </Link>
          <span className="ml-3 hidden text-[11px] text-white/40 sm:inline">crm.boost4life.ro</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-white/10"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-b4l-blue text-xs font-semibold text-white">
                {initials}
              </div>
              <div className="text-left">
                <div className="text-xs font-medium">{user.name}</div>
                {/* Funcția, nu rolul. „admin" e un termen de sistem — nu-l recunoaște
                    nimeni ca fiind meseria lui. Cine n-are funcția completată vede rolul. */}
                <div className="text-[10px] text-white/50">{user.jobTitle || user.role}</div>
              </div>
            </button>
            {open && (
              <div className="absolute right-0 top-full z-40 mt-1 w-56 rounded-md border border-b4l-border bg-b4l-surface-ridicata text-b4l-text shadow-lg">
                {/*
                  Tema (12.10.2026). Doar pentru admini cat timp paginile trec pe paleta noua
                  (etapa B a update-ului de design): una nemutata inca ar arata pe jumatate in
                  tema intunecata, iar ceilalti n-ar sti de ce.
                */}
                {user.role === 'admin' && (
                  <div className="border-b border-b4l-border px-3 py-2">
                    <div className="mb-1.5 text-eticheta uppercase text-b4l-text-dim">
                      Temă · în lucru
                    </div>
                    <div className="grid grid-cols-3 gap-1" role="group" aria-label="Temă">
                      {(
                        [
                          ['luminos', 'Luminoasă', Sun],
                          ['intunecat', 'Întunecată', Moon],
                          ['sistem', 'Ca sistemul', Monitor],
                        ] as const
                      ).map(([cheie, eticheta, Icon]) => (
                        <button
                          key={cheie}
                          type="button"
                          aria-pressed={tema === cheie}
                          title={eticheta}
                          onClick={() => {
                            alegeTema(cheie)
                            setTema(cheie)
                          }}
                          className={`flex flex-col items-center gap-0.5 rounded-md border px-1 py-1.5 text-[11px] ${
                            tema === cheie
                              ? 'border-b4l-blue bg-b4l-blue-light text-b4l-blue-dark'
                              : 'border-b4l-border hover:bg-b4l-bg'
                          }`}
                        >
                          <Icon size={14} />
                          {eticheta}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false)
                        navigate('/ghid-ui')
                      }}
                      className="mt-1.5 w-full rounded-md px-1 py-1 text-left text-mic text-b4l-blue-dark hover:bg-b4l-bg"
                    >
                      Ghid interfață — piesele comune →
                    </button>
                  </div>
                )}
                {/* Legatura casutei, scoasa din bara laterala odata cu Mail (22.10.2026). */}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    navigate('/mailul-meu')
                  }}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-b4l-bg"
                >
                  Căsuța de mail
                </button>
                {poateToken && (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false)
                      navigate('/tokenul-meu')
                    }}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-b4l-bg"
                  >
                    Tokenul meu de API
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    navigate('/change-password')
                  }}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-b4l-bg"
                >
                  Schimbă parola
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false)
                    void onLogout()
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-b4l-red hover:bg-b4l-red-light"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  )
}

// =============================================================================
// NOTIFICATION BELL
// =============================================================================

interface NotificationItem {
  id: number
  kind: string
  title: string
  body: string | null
  link: string | null
  readAt: string | null
  createdAt: string
  actor: { id: number; name: string } | null
}

function NotificationBell() {
  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const navigate = useNavigate()
  const unreadQuery = useQuery<{ count: number }>({
    queryKey: ['notifications-unread'],
    queryFn: async () => (await api.get('/notifications/unread-count')).data,
    refetchInterval: 30_000,
  })

  const listQuery = useQuery<{ notifications: NotificationItem[] }>({
    queryKey: ['notifications-list'],
    queryFn: async () => (await api.get('/notifications')).data,
    enabled: open,
  })

  const markRead = useCitesteNotificarea()
  const markAllRead = useCitesteToateNotificarile()

  // Close popover on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const unread = unreadQuery.data?.count ?? 0
  const items = listQuery.data?.notifications ?? []

  const handleClick = (n: NotificationItem) => {
    if (!n.readAt) markRead.mutate(n.id)
    if (n.link) navigate(n.link)
    setOpen(false)
  }

  return (
    <div ref={popoverRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10"
        title={`${unread} notificare${unread === 1 ? '' : 'i'} necitite`}
      >
        <svg
          className="h-5 w-5 text-white/80"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-b4l-red px-1 text-[9px] font-bold leading-none text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        /*
          Pe telefon panoul se lipeste de marginile ecranului; pe ecran mare ramane un dropdown
          agatat de clopotel.

          Inainte era `right-0 w-80` peste tot. Clopotelul nu sta la marginea din dreapta — are
          avatarul si numele dupa el — asa ca cei 320px se intindeau spre stanga si ieseau din
          ecran: pe un telefon de 360px se vedea „tan ti-a atribuit", cu inceputul randului taiat.
        */
        <div className="fixed inset-x-2 top-14 z-30 rounded-md border border-b4l-border bg-white text-b4l-text shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1 sm:w-80">
          <div className="flex items-center justify-between border-b border-b4l-border px-3 py-2">
            <span className="text-sm font-semibold">Notificări</span>
            {unread > 0 && (
              <button
                onClick={() => markAllRead.mutate()}
                className="text-xs text-b4l-blue-dark hover:underline"
              >
                Marchează toate citite
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="p-4 text-center text-xs text-b4l-text-muted">
                Nicio notificare {listQuery.isLoading ? '— se încarcă...' : ''}
              </p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`block w-full border-b border-b4l-border px-3 py-2 text-left text-xs last:border-0 hover:bg-b4l-bg-soft ${
                    // Solicitarea necitita, in rosu — ca pe Acasa (19.10.2026). Task-ul terminat, in
                    // verde, citit sau nu (21.10.2026).
                    esteTaskTerminat(n.kind)
                      ? n.readAt
                        ? 'border-l-[3px] border-l-b4l-green'
                        : 'border-l-[3px] border-l-b4l-green bg-b4l-green-light'
                      : n.readAt
                        ? ''
                        : esteSolicitare(n.kind)
                          ? 'border-l-[3px] border-l-b4l-red bg-b4l-red-light'
                          : 'bg-b4l-blue-light/20'
                  }`}
                >
                  {!n.readAt && esteSolicitare(n.kind) && (
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-b4l-red-dark">
                      Solicitare nouă
                    </div>
                  )}
                  <div
                    className={`${n.readAt ? '' : 'font-semibold'} ${
                      esteTaskTerminat(n.kind)
                        ? 'text-b4l-green-dark'
                        : !n.readAt && esteSolicitare(n.kind)
                          ? 'text-b4l-red-dark'
                          : ''
                    }`}
                  >
                    {n.title}
                  </div>
                  {n.body && (
                    <div className="mt-0.5 line-clamp-2 text-b4l-text-muted">{n.body}</div>
                  )}
                  <div className="mt-1 text-[10px] text-b4l-text-dim">
                    {new Date(n.createdAt).toLocaleString('en-GB')}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
